import { useEffect } from 'react';

/**
 * Ambient (idle) motion for the AnalyticalField — subtle, premium, non-looping-gimmick.
 * Runs on an INNER group (class .af-ambient) so it composes with the OUTER scroll
 * parallax. One rAF loop; all motion is cyclic-and-slow; reduced-motion → static.
 *
 * 8-axis radar: relief contours breathe slowly (the "folded topographic membrane"
 * reads alive), the dashed echo ring drifts, and the hub crosshair rotates
 * imperceptibly. NO spinning, no obvious loop. The coral rimline profile is
 * analytical data-ink and stays fixed.
 */
export function useAmbientMotion(rootRef, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return undefined;

    const field = root.querySelector('.af-ambient');
    if (!field) return undefined;

    const relief = field.querySelector('.af-relief');
    const echo = field.querySelector('.af-echo');
    const hub = field.querySelector('.af-hub');
    const axes = field.querySelector('.af-axes');

    const t0 = performance.now();
    let raf = 0;
    let active = true;

    const step = (now) => {
      if (!active) {
        raf = requestAnimationFrame(step);
        return;
      }
      const t = (now - t0) / 1000;

      // 1. water-drop membrane breathing — the internal relief surface is alive with
      //    extremely slow, asymmetric X/Y deformation + a tiny center drift. It mimics
      //    surface tension in a shallow water droplet: alive when watched, nearly
      //    static at a glance. Distinct long periods/phases for X, Y, and translation.
      //    NEVER rotates the membrane. (The coral data outline stays fixed.)
      if (relief) {
        const cx = parseFloat(field.getAttribute('data-cx')) || 0;
        const cy = parseFloat(field.getAttribute('data-cy')) || 0;
        const TAU = Math.PI * 2;
        const sx = 1 + 0.007 * Math.sin((TAU * t) / 28);              // ±0.7% X (28s)
        const sy = 1 + 0.0055 * Math.sin((TAU * t) / 34 + 1.3);       // ±0.55% Y (34s, phase-shifted)
        const dx = 1.1 * Math.sin((TAU * t) / 26 + 0.6);              // ±1.1px X drift (26s)
        const dy = 0.8 * Math.sin((TAU * t) / 31 + 2.1);              // ±0.8px Y drift (31s, phase-shifted)
        relief.setAttribute('transform',
          `translate(${(cx + dx).toFixed(2)} ${(cy + dy).toFixed(2)}) ` +
          `scale(${sx.toFixed(4)} ${sy.toFixed(4)}) translate(${(-cx).toFixed(2)} ${(-cy).toFixed(2)})`);
        // a whisper of opacity ripple tied to the X period (keeps relief character)
        relief.style.opacity = (1 + 0.015 * Math.sin((TAU * t) / 28)).toFixed(3);
      }

      // 2. dashed echo ring — dashoffset drift (48s)
      if (echo) {
        const off = -((t * 12) % 48);
        echo.setAttribute('stroke-dashoffset', off.toFixed(1));
      }

      // 3. hub crosshair — imperceptible rotation (120s)
      if (hub) {
        const cx = parseFloat(hub.getAttribute('data-cx'));
        const cy = parseFloat(hub.getAttribute('data-cy'));
        hub.setAttribute('transform', `rotate(${(t * 3 % 360).toFixed(2)} ${cx} ${cy})`);
      }

      // 4. axes — imperceptible shared breathing (ties spokes to the membrane)
      if (axes) {
        const s = 1 + 0.006 * Math.sin((2 * Math.PI * t) / 30);
        axes.setAttribute('opacity', (0.9 + Math.sin((2 * Math.PI * t) / 30) * 0.08).toFixed(3));
      }

      raf = requestAnimationFrame(step);
    };

    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(([entry]) => {
        active = entry.isIntersecting;
      }, { rootMargin: '160px 0px' });
      io.observe(root);
    }
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
    };
  }, [rootRef, enabled]);
}